import { useState } from 'react';

const AttachmentPreview = ({ attachments, onDelete, showDelete = false }) => {
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Get backend URL from environment
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const getFileIcon = (mimeType) => {
    const icons = {
      'application/pdf': '📄',
      'application/msword': '📝',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
      'application/vnd.ms-powerpoint': '📊',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '📊',
      'application/vnd.ms-excel': '📈',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📈',
      'text/plain': '📄'
    };
    return icons[mimeType] || '📎';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const openImageGallery = (index) => {
    setSelectedImageIndex(index);
    setImageGalleryOpen(true);
  };

  const closeImageGallery = () => {
    setImageGalleryOpen(false);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => 
      prev < attachments.gallery.length - 1 ? prev + 1 : 0
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => 
      prev > 0 ? prev - 1 : attachments.gallery.length - 1
    );
  };

  if (!attachments) return null;

  return (
    <div className="space-y-6">
      {/* Banner Image */}
      {attachments.banner && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            🖼️ Event Banner
          </h3>
          <div className="relative">
            <div className="aspect-[2/1] bg-gray-100 rounded-xl overflow-hidden">
              <img
                src={`${backendUrl}/api/files/${attachments.banner.id}`}
                alt="Event Banner"
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => openImageGallery(0)}
                onError={(e) => {
                  console.error('Error loading banner image:', e);
                  console.warn(`Banner image failed to load: ${backendUrl}/api/files/${attachments.banner.id}`);
                  // Hide the broken image and show placeholder div instead
                  e.target.style.display = 'none';
                  const container = e.target.parentElement;
                  if (container && !container.querySelector('.banner-placeholder')) {
                    const placeholder = document.createElement('div');
                    placeholder.className = 'banner-placeholder w-full h-full flex items-center justify-center bg-gray-100';
                    placeholder.innerHTML = `
                      <div class="text-center">
                        <div class="text-4xl mb-2 text-gray-400">🖼️</div>
                        <p class="text-gray-500 text-sm">Banner Image Not Available</p>
                      </div>
                    `;
                    container.appendChild(placeholder);
                  }
                }}
                onLoad={() => {
                  console.log('✅ Banner image loaded successfully');
                }}
              />
            </div>
            {showDelete && onDelete && (
              <button
                onClick={() => onDelete(attachments.banner.id)}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                title="Delete banner"
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}

      {/* Gallery Images */}
      {attachments.gallery && attachments.gallery.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            📸 Event Gallery ({attachments.gallery.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {attachments.gallery.map((image, index) => (
              <div key={image.id} className="relative group">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={image.thumbnail_path ? `${backendUrl}/api/files/thumbnail/${image.id}` : `${backendUrl}/api/files/${image.id}`}
                    alt={image.description || `Gallery image ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => openImageGallery(index)}
                    onError={(e) => {
                      console.error('Error loading gallery image:', e);
                      // Hide the broken image and show placeholder
                      e.target.style.display = 'none';
                      const container = e.target.parentElement;
                      if (container && !container.querySelector('.gallery-placeholder')) {
                        const placeholder = document.createElement('div');
                        placeholder.className = 'gallery-placeholder w-full h-full flex items-center justify-center bg-gray-100';
                        placeholder.innerHTML = `
                          <div class="text-center">
                            <div class="text-2xl text-gray-400">📷</div>
                            <p class="text-gray-400 text-xs mt-1">Not Available</p>
                          </div>
                        `;
                        container.appendChild(placeholder);
                      }
                    }}
                  />
                </div>
                {showDelete && onDelete && (
                  <button
                    onClick={() => onDelete(image.id)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete image"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Attachments */}
      {attachments.attachments && attachments.attachments.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            📎 Downloads & Resources ({attachments.attachments.length})
          </h3>
          <div className="space-y-2">
            {attachments.attachments.map((attachment) => (
              <div key={attachment.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                {/* File Icon */}
                <div className="text-2xl text-gray-600">
                  {getFileIcon(attachment.mime_type)}
                </div>
                
                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-gray-900 truncate">
                      {attachment.file_name}
                    </p>
                    <span className="text-sm text-gray-500">
                      ({formatFileSize(attachment.file_size)})
                    </span>
                  </div>
                  {attachment.description && (
                    <p className="text-sm text-gray-600 mt-1">
                      {attachment.description}
                    </p>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <a
                    href={`${backendUrl}/api/files/download/${attachment.id}`}
                    download
                    className="px-3 py-1 bg-teal-600 text-white rounded-md text-sm hover:bg-teal-700 transition-colors"
                    title="Download file"
                  >
                    ⬇️ Download
                  </a>
                  {showDelete && onDelete && (
                    <button
                      onClick={() => onDelete(attachment.id)}
                      className="px-3 py-1 bg-red-100 text-red-600 rounded-md text-sm hover:bg-red-200 transition-colors"
                      title="Delete attachment"
                    >
                      🗑️ Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {imageGalleryOpen && attachments.gallery && attachments.gallery.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={closeImageGallery}
              className="absolute top-4 right-4 w-10 h-10 bg-white text-black rounded-full flex items-center justify-center text-xl hover:bg-gray-100 transition-colors z-10"
            >
              ×
            </button>
            
            {/* Previous Button */}
            {attachments.gallery.length > 1 && (
              <button
                onClick={prevImage}
                className="absolute left-4 w-12 h-12 bg-white bg-opacity-20 text-white rounded-full flex items-center justify-center text-xl hover:bg-opacity-40 transition-colors z-10"
              >
                ‹
              </button>
            )}
            
            {/* Next Button */}
            {attachments.gallery.length > 1 && (
              <button
                onClick={nextImage}
                className="absolute right-4 w-12 h-12 bg-white bg-opacity-20 text-white rounded-full flex items-center justify-center text-xl hover:bg-opacity-40 transition-colors z-10"
              >
                ›
              </button>
            )}
            
            {/* Image */}
            <div className="max-w-4xl max-h-full">
              <img
                src={`${backendUrl}/api/files/${attachments.gallery[selectedImageIndex]?.id}`}
                alt={attachments.gallery[selectedImageIndex]?.description || 'Gallery image'}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  console.error('Error loading full-size image:', e);
                }}
              />
            </div>
            
            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full">
              {selectedImageIndex + 1} / {attachments.gallery.length}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!attachments.banner && (!attachments.gallery || attachments.gallery.length === 0) && (!attachments.attachments || attachments.attachments.length === 0)) && (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-4">📎</div>
          <p>No attachments available</p>
        </div>
      )}
    </div>
  );
};

export default AttachmentPreview;